import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';
import { mod } from 'react-swipeable-views-core';
import { withStyles } from '@material-ui/core/styles';
import {
  IconButton,
  ButtonBase,
  Typography,
  Dialog,
  CircularProgress
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import LoadingOverlayWrapper from 'components/layout/LoadingOverlayWrapper';
import { ImgWithRetry } from 'components/layout';
import ImageSourceSelection from 'components/layout/ImageCarousel/ImageSourceSelection';
import ThumbnailNav from 'components/layout/ImageCarousel/ThumbnailNav';

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const ImageCarousel = ({ classes, imageSources, isLoading, square, showAsGrid, gridThumbnailCount }) => {
  const [selectedSourceIndex, setSelectedSourceIndex] = useState(null);
  const [mappedImages, setMappedImages] = useState([]);
  const [imageIndex, setImageIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const virtualIndex = mod(imageIndex, mappedImages.length);

  useEffect(() => {
    if (!imageSources?.length) return;
    const joinedImages = imageSources.map(source => {
      return {
        ...source,
        images: source.images.map((img, index) => {
          return {
            ...img,
            thumbnailUrl: img.thumbnailUrl ?? img.imageUrl,
            isFirstOfSource: index === 0,
            isLastOfSource: index === source.images.length - 1
          }
        })
      }
    }).reduce((joined, source) => {
      return joined.concat(source.images);
    }, []);
    setMappedImages(joinedImages);
    setImageIndex(0);
  }, [imageSources]);

  function handleNextImage() {
    const nextIndex = imageIndex + 1;
    setImageIndex(nextIndex);
  }
  function handlePrevImage() {
    const prevIndex = imageIndex - 1;
    setImageIndex(prevIndex);
  }

  const GridThumbnailsRenderer = () => {
    if (!mappedImages?.length) return null;
    const thumbnails = mappedImages.slice(0, gridThumbnailCount);
    const excessImageCount = mappedImages.length - gridThumbnailCount;
    return (
      <div className={classes.gridThumbnailContainer}>
        {thumbnails.map((img, index) => {
          return (
            <ButtonBase 
              key={index}
              onClick={() => handleOpen(index)}
            >
              <ImgWithRetry alt={`propertyphoto ${index}`} src={img.thumbnailUrl} style={{
                  width: thumbnails.length === 1 ? 170 : 80, 
                  height: thumbnails.length === 1 ? 100 : 60, 
                  border: '1px solid #fff'
                }} 
                retryCount={2}
              />
              {index + 1 === gridThumbnailCount && excessImageCount > 0 &&
                <div className={classes.excessImagesOverlay}>
                  <Typography variant='h6' className={classes.excessImagesOverlayCount}>{'+' + (excessImageCount + 1)}</Typography>
                </div>
              }
            </ButtonBase>
          )
        })}
      </div>
    )
  }

  function slideRenderer({ index, key }) {
    const virtualIndex = mod(index, mappedImages.length);
    return (
      <div key={key} style={{height: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
        {selectedSourceIndex == null &&
          <ImgWithRetry alt={`propertyphoto ${virtualIndex}`} src={mappedImages[virtualIndex].thumbnailUrl} className={classes.propImage} retryCount={2}/>
        }
      </div>
    )
  }

  function handleOpen(index) {
    setImageIndex(index);
    setDialogOpen(true)
  }

  const handleClose = () => {
    setDialogOpen(false);
  };

  const ImageRenderer = () => {
    const { imageUrl, caption } = mappedImages[virtualIndex];
    return (
      <div className={classes.imageContainer} >
        {selectedSourceIndex == null
          ? <>
              <div className={classes.imageWrapper}>
                <ImgWithRetry alt={`propertyphoto ${virtualIndex}`} src={imageUrl} className={classes.propImage} retryCount={2}/>
                {mappedImages.length > 1 &&
                  <IndexDisplay index={virtualIndex} />
                }
              </div>
              {caption &&
                <div className={classes.captionContainer}>
                  <Typography className={classes.captionText}>{caption}</Typography>
                </div>
              }
            </>
          : <CircularProgress />
        }
      </div>
    )
  }

  const handleSelectedSourceChange = (e) => {
    const selectedValue = e.target.value;
    const index = mappedImages.findIndex(img => img.source === selectedValue);
    setSelectedSourceIndex(index);
  };

  const IndexDisplay = ({ index} ) => {
    return (
      <div className={classes.indexDisplay}>       
        <Typography className={classes.indexDisplayText}>{index + 1} of {mappedImages.length}</Typography>
      </div>    
    )
  }

  return (
    <div className={classes.root} style={square ? {} : {borderRadius: 8}}>
      <LoadingOverlayWrapper
        active={isLoading}
        message='Loading images...'
      >
        {showAsGrid
          ? <GridThumbnailsRenderer />
          : <>
              {mappedImages.length &&
                <VirtualizeSwipeableViews
                  index={imageIndex}
                  slideRenderer={slideRenderer}
                  slideClassName={classes.slide}
                />
              }
              <div
                className={classes.overlayContainer}
                onKeyDown={e => {
                  if (mappedImages.length > 1) {
                    e.keyCode === 37 && handlePrevImage();
                    e.keyCode === 39 && handleNextImage();
                  }
                }}  
              >
                {mappedImages.length > 1 &&
                  <IconButton
                    onClick={() => handlePrevImage()}
                    className={classes.icon}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                }
                <div className={classes.fullscreenButton} onClick={() => handleOpen(virtualIndex)}></div>
                {mappedImages.length > 1 &&
                  <>
                    <IconButton
                      onClick={() => handleNextImage()}
                      className={classes.icon}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                    <IndexDisplay index={virtualIndex} />
                  </>
                }
              </div>
            </>
        }
      </LoadingOverlayWrapper>

      {/* Fullscreen viewer */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth={'xl'}
        onKeyDown={e => {
          e.keyCode === 27 && handleClose();
          if (mappedImages.length > 1) {
            e.keyCode === 37 && handlePrevImage();
            e.keyCode === 39 && handleNextImage();
          }
        }}
        PaperProps={{style: {width: '100%'}}}
      >
        {mappedImages.length &&
          <>
            <div className={classes.controlContainer}>
                <ImageSourceSelection
                  value={mappedImages[virtualIndex].source}
                  imageSources={imageSources}
                  onChange={handleSelectedSourceChange}
                />
                <div style={{ flexGrow: 1 }}></div>
                <IconButton className={classNames(classes.icon)} onClick={handleClose}>
                  <CloseIcon/>
                </IconButton>
              </div>
              {/* <VirtualizeSwipeableViews
                enableMouseEvents
                index={imageIndex}
                slideRenderer={slideRendererFull}
                slideClassName={classes.slideFull}
              /> */} {/* Use this for transition managed by swipeable-views, use ImageRenderer for no transition */}
            <ImageRenderer imageIndex={imageIndex} handleClose={handleClose} />
            {mappedImages.length > 1 &&
              <>
                <ThumbnailNav
                  mappedImages={mappedImages}
                  imageIndex={imageIndex}
                  setImageIndex={setImageIndex}
                  selectedSourceIndex={selectedSourceIndex}
                  setSelectedSourceIndex={setSelectedSourceIndex}
                />
                <div className={classes.buttonContainer}>
                  <IconButton
                    onClick={() => handlePrevImage()}
                      className={classNames(classes.icon, classes.iconFullscreen)}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleNextImage()}
                      className={classNames(classes.icon, classes.iconFullscreen)}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </div>
                </>
            }
          </>
        }
      </Dialog>
    </div>
  )  
};

const styles = theme => ({
  root: {
    position: 'relative',
    // height: 288
    // borderRadius: 8,
    overflow: 'hidden'
  },
  overlayContainer: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center'
  },
  buttonContainer: {
    position: 'absolute',
    height: '48px',
    width: '100%',
    // top: 'calc(50% - 24px)', // centered to whole dialog
    top: 'calc(50% - 56px)', // centered to image section only (exclude thumbnails)
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  icon: {
    color: '#fff',
    background: '#333333a8',
    '&:hover': {
      background: '#333333db'
    },
    margin: 4
  },
  iconFullscreen: {
    margin: 16
  },
  fullscreenButton: {
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    margin: '0 4px'
  },
  slide: {
    height: 288,
    color: '#fff',
    background: '#000',
    overflow: 'hidden'
  },
  slideFull: {
    height: 'calc(90vh - 168px)', // 90vh - height of thumbnail section
    color: '#fff',
    background: '#000',
    overflow: 'hidden'
  },
  propImage: {
    objectFit: 'scale-down',
    maxHeight: '100%',
    maxWidth: '100%',
    // maxHeight: 'calc(90vh - 168px)'
  },
  imageContainer: {
    height: 'calc(90vh - 168px)',
    overflow: 'hidden',
    backgroundColor: '#000',
    display: 'flex', 
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1920,
    flexDirection: 'column',
    position: 'relative'
  },
  imageWrapper: {
    maxHeight: '100%',
    maxWidth: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: 120,
    minWidth: 160
  },
  indexDisplay: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    top: '8px',
    right: '8px',
    padding: '0 6px',
    borderRadius: 4
  },
  indexDisplayText: {
    color: '#fff',
    whiteSpace: 'nowrap'
  },
  excessImagesOverlay: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    backgroundColor: 'rgb(0, 0, 0, 0.4)',
    alignContent: 'center'
  },
  excessImagesOverlayCount: {
    color: '#fff'
  },
  controlContainer: {
    width: '100%',
    backgroundColor: 'rgba(25, 25, 25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 8
  },
  captionContainer: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    bottom: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '0 6px',
    borderRadius: 4
  },
  captionText: {
    color: '#fff',
    textAlign: 'center'
  },
  gridThumbnailContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

export default withStyles(styles)(ImageCarousel);